import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PlanState } from './plan.reducer';
import { SubscriptionPlanType, SubscriptionPlan, PlanFeatures, PlanStatistics } from '../../models/stripe.models';
import { isFreemiumTier, isGrowthOrHigherTier, isStarterTier } from 'src/app/utils/plan-tier.utils';

export const selectPlanState = createFeatureSelector<PlanState>('plan');

export const selectPlanData = createSelector(
    selectPlanState,
    (state: PlanState): SubscriptionPlan | null => state?.data ?? null
);

export const selectPlanFeatures = createSelector(
    selectPlanData,
    (data): PlanFeatures | undefined => data?.features
);

export const selectPlanStatistics = createSelector(
    selectPlanData,
    (data): PlanStatistics | undefined => data?.statistics
);

export const selectPlanIdentifier = createSelector(
    selectPlanData,
    (data): string | undefined => data?.identifier
);

/**
 * FlyLearning entitlement comes from the `plans.has_fly_learning` flag
 * (managed by ops in CMC). This is the single source of truth — never
 * gate FlyLearning on plan slugs/names.
 */
export const selectHasFlyLearning = createSelector(
    selectPlanData,
    (data): boolean => data?.has_fly_learning === true
);

export const selectPlanLoading = createSelector(
    selectPlanState,
    (state: PlanState): boolean => state?.loading ?? false
);

/**
 * Returns the user-friendly display name for the current plan.
 * This includes the billing cycle (Monthly/Yearly) and special plan names.
 */
export const selectPlanDisplayName = createSelector(
    selectPlanData,
    (plan: SubscriptionPlan | null): string => {
        if (!plan) return 'Freemium';

        const identifier = plan.identifier?.toLowerCase();
        const title = plan.title;

        switch (identifier) {
            case 'freemium':
            case 'free':
                return 'Freemium';
            case 'starter':
                return 'Starter (Monthly Plan)';
            case 'starter-yearly':
                return 'Starter (Yearly Plan)';
            case 'growth':
                return 'Growth (Monthly Plan)';
            case 'growth-yearly':
                return 'Growth (Yearly Plan)';
            case 'sales-pro':
            case 'sales-pro-monthly':
                return 'Business Pro - Monthly Plan';
            case 'sales-pro-yearly':
                return 'Business Pro - Yearly Plan';
            case 'appsumo-growth-lifetime':
                return 'AppSumo Lifetime Growth';
            case 'dealfuel-growth-lifetime':
                return 'DealFuel Lifetime Growth';
            case 'pro-plan-teams-smb':
                return 'Pro Plan Teams';
            case 'pro-plan-teams-ent':
                return 'Pro Plan Teams';
            default:
                return title || 'Freemium';
        }
    }
);

/**
 * Returns the SubscriptionPlanType enum value for the current plan.
 * Used for type-safe plan comparisons.
 */
export const selectPlanType = createSelector(
    selectPlanData,
    (plan: SubscriptionPlan | null): SubscriptionPlanType => {
        if (!plan) return SubscriptionPlanType.Freemium;

        const identifier = plan.identifier?.toLowerCase();

        switch (identifier) {
            case 'freemium':
            case 'free':
                return SubscriptionPlanType.Freemium;
            case 'starter':
                return SubscriptionPlanType.Starter;
            case 'starter-yearly':
                return SubscriptionPlanType.YearlyStarter;
            case 'growth':
                return SubscriptionPlanType.Growth;
            case 'growth-yearly':
            case 'appsumo-growth-lifetime':
            case 'dealfuel-growth-lifetime':
                return SubscriptionPlanType.YearlyGrowth;
            case 'sales-pro':
                return SubscriptionPlanType.SalesPro;
            case 'sales-pro-monthly':
                return SubscriptionPlanType.MonthlySalesPro;
            case 'sales-pro-yearly':
                return SubscriptionPlanType.YearlySalesPro;
            case 'pro-plan-teams-smb':
                return SubscriptionPlanType.ProPlanTeamsSMB;
            case 'pro-plan-teams-ent':
                return SubscriptionPlanType.ProPlanTeamsEnt;
            default:
                return SubscriptionPlanType.Freemium;
        }
    }
);

/**
 * Returns a simplified display name for use in the header/menu.
 * Shows just the base plan name without billing cycle details.
 */
export const selectPlanMenuDisplayName = createSelector(
    selectPlanData,
    (plan: SubscriptionPlan | null): string => {
        if (!plan) return 'Freemium';

        const identifier = plan.identifier?.toLowerCase();

        switch (identifier) {
            case 'freemium':
            case 'free':
                return 'Freemium';
            case 'starter':
            case 'starter-yearly':
                return 'Starter';
            case 'growth':
            case 'growth-yearly':
                return 'Growth';
            case 'appsumo-growth-lifetime':
                return 'AppSumo Lifetime Growth';
            case 'dealfuel-growth-lifetime':
                return 'DealFuel Lifetime Growth';
            case 'sales-pro':
            case 'sales-pro-monthly':
            case 'sales-pro-yearly':
                return 'Business Pro';
            case 'pro-plan-teams-smb':
                return 'Pro Teams - SMB';
            case 'pro-plan-teams-ent':
                return 'Pro Teams - ENT';
            default:
                return plan.title || 'Freemium';
        }
    }
);

/**
 * Returns whether the user can upgrade their plan.
 * Sales Pro and Team plans cannot upgrade further.
 */
export const selectCanUpgrade = createSelector(
    selectPlanData,
    (plan: SubscriptionPlan | null): boolean => {
        if (!plan) return true;

        const identifier = plan.identifier?.toLowerCase();
        const noUpgradeIdentifiers = [
            'sales-pro',
            'sales-pro-yearly',
            'sales-pro-monthly',
            'pro-plan-teams-smb',
            'pro-plan-teams-ent'
        ];

        return !noUpgradeIdentifiers.includes(identifier);
    }
);

/**
 * Returns whether the user is on the Freemium plan.
 */
export const selectIsFreemium = createSelector(selectPlanType, isFreemiumTier);

/**
 * Returns whether the user is on a Starter plan (monthly or yearly).
 */
export const selectIsStarter = createSelector(selectPlanType, isStarterTier);

/**
 * Returns whether the user is on a Growth or higher plan.
 * Tier membership is defined once in plan-tier.utils.ts.
 */
export const selectIsGrowthOrHigher = createSelector(selectPlanType, isGrowthOrHigherTier);
